import { useState, useEffect, useRef } from "react";
import {
  Button,
  Popover,
  Modal,
  Tabs,
  Form,
  Input,
  Select,
  Checkbox,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { ReactComponent as HelpIcon } from "../../assets/images/Help.svg";
import HerdsIcon from "../../assets/images/Herd.svg";
import VenceIcon from "../../assets/images/Vence.svg";
import CalendarIcon from "../../assets/images/Calendar.svg";
import LandmarkIcon from "../../assets/images/Landmarks.svg";
import MapIcon from "../../assets/images/Map.svg";
import { ReactComponent as Paddock } from "../../assets/images/landmark/1.5/Paddock.svg";
import { ReactComponent as Structure } from "../../assets/images/landmark/1.5/Structure.svg";
import { ReactComponent as Gate } from "../../assets/images/landmark/1.5/Gate.svg";
import { ReactComponent as Corral } from "../../assets/images/landmark/1.5/Corral.svg";
import { ReactComponent as Gateway } from "../../assets/images/landmark/1.5/Gateway.svg";
import { ReactComponent as Trail } from "../../assets/images/landmark/1.5/Trail.svg";
import { ReactComponent as Road } from "../../assets/images/landmark/1.5/Road.svg";
import { ReactComponent as Pump } from "../../assets/images/landmark/1.5/Pump.svg";
import { ReactComponent as Water } from "../../assets/images/landmark/1.5/Water.svg";
import { ReactComponent as River } from "../../assets/images/landmark/1.5/River.svg";
import { ReactComponent as Burn } from "../../assets/images/landmark/1.5/Burn.svg";
import { ReactComponent as Feed } from "../../assets/images/landmark/1.5/Feed.svg";
import { ReactComponent as Area } from "../../assets/images/landmark/1.5/Area.svg";
import "./SideBar.scss";
import PopoverTitle from "../common/PopoverTitle";
import CreateHerd from "./CreateHerd/CreateHerd";
import clsx from "clsx";
import HerdsToDisplay from "./HerdsToDisplay/HerdsToDisplay";
import CreateVence from "./CreateVence/CreateVence";
import CreateVenceConfirm from "./CreateVenceConfirm/CreateVenceConfirm";

const { TabPane } = Tabs;

function SideBar(props) {
  const [currentMenu, setCurrentMenu] = useState("Main");
  const {
    landmark,
    setLandmark,
    currentStepDetails,
    setCurrentStep,
    setHerdMarkerDetails,
    herdMarkerDetails,
    venceInclusionDetails,
    venceExclusionDetails,
  } = props;
  const sidebarRef = useRef(null);

  // Landmark setting up tutorial
  const [modalNewLandmarkVisible, setModalNewLandmarkVisible] = useState(false);
  const [createLandmarkPopoverVisible, setCreateLandmarkPopoverVisible] =
    useState(false);
  const [landmarkStep4Popover, setLandmarkStep4Popover] = useState(false);
  const [landmarkStep7Popover, setLandmarkStep7Popover] = useState(false);
  const [landmarkStep10Popover, setLandmarkStep10Popover] = useState(false);
  const [landmarkStep14Popover, setLandmarkStep14Popover] = useState(false);

  // Herd setting up tutorial
  const [createHerdModal, setCreateHerdModal] = useState(false);
  const [herdStep4Popover, setHerdStep4Popover] = useState(false);
  const [herdStep5Popover, setHerdStep5Popover] = useState(false);
  const [herdStep9Popover, setHerdStep9Popover] = useState(false);
  const [herdStep10Popover, setHerdStep10Popover] = useState(false);

  // Creating vences tutorial
  const [venceStep2Popover, setVenceStep2Popover] = useState(false);
  const [venceStep3Popover, setVenceStep3Popover] = useState(false);
  const [venceStep6Popover, setVenceStep6Popover] = useState(false);
  const [venceStep11Popover, setVenceStep11Popover] = useState(false);
  const [createVenceModal, setCreateVenceModal] = useState(false);
  const [venceStep7Popover, setVenceStep7Popover] = useState(false);
  const [venceStep8Popover, setVenceStep8Popover] = useState(false);

  // Assigning vences tutorial
  const [venceAssignStep4Popover, setVenceAssignStep4Popover] = useState(false);

  const functionsWithRefs = {
    createLandmarkPopoverVisible: setCreateLandmarkPopoverVisible,
    landmarkStep4Popover: setLandmarkStep4Popover,
    landmarkStep7Popover: setLandmarkStep7Popover,
    landmarkStep10Popover: setLandmarkStep10Popover,
    modalNewLandmarkVisible: setModalNewLandmarkVisible,
    landmarkStep14Popover: setLandmarkStep14Popover,

    createHerdModal: setCreateHerdModal,
    herdStep4Popover: setHerdStep4Popover,
    herdStep5Popover: setHerdStep5Popover,
    herdStep9Popover: setHerdStep9Popover,
    herdStep10Popover: setHerdStep10Popover,

    venceStep2Popover: setVenceStep2Popover,
    venceStep3Popover: setVenceStep3Popover,
    venceStep6Popover: setVenceStep6Popover,
    venceStep11Popover: setVenceStep11Popover,
    createVenceModal: setCreateVenceModal,
    venceStep7Popover: setVenceStep7Popover,
    venceStep8Popover: setVenceStep8Popover,

    venceAssignStep4Popover: setVenceAssignStep4Popover,
  };

  useEffect(() => {
    if (currentStepDetails.step) {
      currentStepDetails.popoverComponents?.forEach((popoverComponent) => {
        functionsWithRefs[popoverComponent] &&
          functionsWithRefs[popoverComponent](true);
      });
      currentStepDetails.modalComponents?.forEach((modalComponent) => {
        functionsWithRefs[modalComponent] &&
          functionsWithRefs[modalComponent](true);
      });

      // Update the current menu
      if (currentStepDetails.sideMenu)
        setCurrentMenu(currentStepDetails.sideMenu);

      // Scroll to the top/bottom of the sidebar
      if (currentStepDetails.scrollTo === "Top") {
        sidebarRef.current.scrollTop = 0;
      } else if (currentStepDetails.scrollTo === "Bottom") {
        sidebarRef.current.scrollTop = sidebarRef.current.scrollHeight;
      }

      // If same modal is used by multiple steps, then don't close/open it when the step changes. Close using clearSameModalComponents value, when using modal component is not required.
      currentStepDetails.sameModalComponents?.forEach((modalComponent) => {
        functionsWithRefs[modalComponent] &&
          functionsWithRefs[modalComponent](true);
      });
      currentStepDetails.clearSameModalComponents?.forEach((modalComponent) => {
        functionsWithRefs[modalComponent] &&
          functionsWithRefs[modalComponent](false);
      });
    }
    return () => {
      currentStepDetails.popoverComponents?.forEach((popoverComponent) => {
        functionsWithRefs[popoverComponent] &&
          functionsWithRefs[popoverComponent](false);
      });
      currentStepDetails.modalComponents?.forEach((modalComponent) => {
        functionsWithRefs[modalComponent] &&
          functionsWithRefs[modalComponent](false);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepDetails.step]);

  const content = (
    <div>
      <p>
        Click <strong>CREATE LANDMARK.</strong>
      </p>
    </div>
  );

  const contentOfPaddock = (
    <div>
      Select <strong>PADDOCK/HARD FENCE.</strong>
    </div>
  );

  const contentOfGateway = (
    <div>
      Select <strong>GATEWAY.</strong>
    </div>
  );

  const contentOfNewLandmarkModalOfFence = (
    <div>
      <p>
        Let’s give this landmark a name and set its type to{" "}
        <strong>FENCE.</strong>
      </p>
      <p>
        When you’re finished, click <strong>SAVE.</strong>
      </p>
    </div>
  );

  const contentOfNewLandmarkModalOfGateway = (
    <div>
      <p>
        Give this landmark a name and set its Type to <strong>GATEWAY.</strong>
      </p>
      <p>
        Click <strong>SAVE</strong> when you’re done.
      </p>
    </div>
  );

  const contentOfImportKML = (
    <div>
      <p>
        When setting up your own ranch, if you already have a KML file with your
        paddocks and other landmarks, you’d upload it here.
      </p>
      <div>
        <Button
          type="primary"
          size="middle"
          style={{ left: "80%" }}
          onClick={() => setCurrentStep((step) => step + 1)}
          className="tutorial-guide-button"
        >
          Next
        </Button>
      </div>
    </div>
  );

  const contentOfCreateHerd = (
    <div>
      Click <strong>CREATE HERD FROM INVENTORY.</strong>
    </div>
  );

  const contentOfCreateVence = (
    <div>
      {currentStepDetails.step === 2 ? (
        <p>
          Start by clicking <strong>CREATE VENCE.</strong>
        </p>
      ) : (
        <p>
          Click <strong>CREATE VENCE.</strong>
        </p>
      )}
    </div>
  );

  const contentOfAssignVenceToHerd = (
    <div>
      Start with <strong>ASSIGN VENCE TO HERD.</strong>
    </div>
  );

  const onBackButtonClick = () => {
    setCurrentMenu("Main");
    setLandmark({
      type: "",
      shape: 0,
    });
    // setModalNewLandmarkVisible(false);
  };

  const onCreateLandmark = (type, shape) => {
    setLandmark({
      type: type,
      shape: shape,
    });
    if (type !== "Fence" && type !== "Gateway") return; // Condition to stop the Other landmarks.
    setCurrentMenu("CreateLandmarkConfirm");
  };

  const onCreateLandmarkConfirm = () => {
    setModalNewLandmarkVisible(true);
  };

  const renderMainMenu = () => {
    return (
      <div className="p-15">
        <div className="sidebar-menu-item">
          <div className="header">
            <img
              src={HerdsIcon}
              style={{ width: "32px", height: "18px" }}
              alt="herds Icon"
            />
            <label className="title">Herds</label>
            <Button
              type="text"
              style={{ marginLeft: "auto" }}
              icon={<HelpIcon className="help-icon" />}
            />
          </div>
          <Popover
            title={PopoverTitle}
            content={contentOfCreateHerd}
            placement="leftTop"
            trigger="hover"
            visible={herdStep4Popover}
          >
            <Button
              block
              onClick={() => {
                setCurrentMenu("CreateHerd");
                setCurrentStep((step) => step + 1);
              }}
              id="create-herd-inventory-button"
              className={clsx("sub-item", {
                "overlay-element-active":
                  currentStepDetails.overlayElementsId?.includes(
                    "create-herd-inventory-button"
                  ),
              })}
            >
              Create Herd from Inventory
            </Button>
          </Popover>
          <Popover
            title={PopoverTitle}
            content={contentOfAssignVenceToHerd}
            placement="leftTop"
            trigger="hover"
            visible={venceAssignStep4Popover}
          >
            <Button
              block
              id="assign-vence-to-herd-button"
              className={clsx("sub-item", {
                  "overlay-element-active":
                    currentStepDetails.overlayElementsId?.includes(
                      "assign-vence-to-herd-button"
                    ),
              })}
              onClick={() => {
                setCurrentStep((step) => step + 1);
              }}
            >
              Assign Vence to Herd
            </Button>
          </Popover>
          <Button
            block
            className="sub-item"
            onClick={() => {
              setCurrentMenu("HerdsToDisplay");
              setCurrentStep((step) => step + 1);
            }}
          >
            Select Herds to Display
          </Button>
          <Button block className="sub-item">
            Select Inventory to Display
          </Button>
        </div>
        <div className="sidebar-menu-item">
          <div className="header">
            <img
              src={VenceIcon}
              style={{ width: "32px", height: "18px" }}
              alt="Vences Icon"
            />
            <label className="title">Vences</label>
            <Button
              type="text"
              style={{ marginLeft: "auto" }}
              icon={<HelpIcon className="help-icon" />}
            />
          </div>
          <Popover
            title={PopoverTitle}
            content={contentOfCreateVence}
            placement="left"
            trigger="hover"
            visible={venceStep2Popover}
          >
            <Button
              block
              onClick={() => {
                setCurrentMenu("CreateVence");
                setCurrentStep((step) => step + 1);
              }}
              id="create-vence-button"
              className={clsx("sub-item", {
                "overlay-element-active":
                  currentStepDetails.overlayElementsId?.includes(
                    "create-vence-button"
                  ),
              })}
            >
              Create Vence
            </Button>
          </Popover>
          <Button block className="sub-item">
            Select Vences to Display
          </Button>
        </div>
        <div className="sidebar-menu-item">
          <div className="header">
            <img
              src={CalendarIcon}
              style={{ width: "32px", height: "18px" }}
              alt="Calendar Icon"
            />
            <label className="title">Calendar</label>
            <Button
              type="text"
              style={{ marginLeft: "auto" }}
              icon={<HelpIcon className="help-icon" />}
            />
          </div>
          <Button block className="sub-item">
            Show Calendar
          </Button>
        </div>
        <div className="sidebar-menu-item">
          <div className="header">
            <img
              src={LandmarkIcon}
              style={{ width: "32px", height: "18px" }}
              alt="Landmark Icon"
            />
            <label className="title">Landmarks</label>
            <Button
              type="text"
              style={{ marginLeft: "auto" }}
              icon={<HelpIcon className="help-icon" />}
            />
          </div>
          <Popover
            title={PopoverTitle}
            content={content}
            placement="leftTop"
            trigger="hover"
            visible={createLandmarkPopoverVisible}
            // onVisibleChange={(visible) => console.log(visible)}
          >
            <Button
              block
              onClick={() => {
                setCurrentMenu("CreateLandmark");
                setCurrentStep((step) => step + 1);
              }}
              id="create-landmark-button"
              className={clsx("sub-item", {
                "overlay-element-active":
                  currentStepDetails.overlayElementsId?.includes(
                    "create-landmark-button"
                  ),
              })}
            >
              Create Landmark
            </Button>
          </Popover>
          <Button block className="sub-item">
            Select Landmarks to Display
          </Button>
        </div>
        <div className="sidebar-menu-item">
          <div className="header">
            <img
              src={MapIcon}
              style={{ width: "32px", height: "18px" }}
              alt="Map Icon"
            />
            <label className="title">Map</label>
            <Button
              type="text"
              style={{ marginLeft: "auto" }}
              icon={<HelpIcon className="help-icon" />}
            />
          </div>
          <Button block className="sub-item">
            Map Options
          </Button>
          <Popover
            title={PopoverTitle}
            content={contentOfImportKML}
            placement="leftTop"
            trigger="hover"
            visible={landmarkStep14Popover}
            // onVisibleChange={(visible) => console.log(visible)}
          >
            <Button
              block
              id="import-kml-button"
              className={clsx("sub-item", {
                "overlay-element-active":
                  currentStepDetails.overlayElementsId?.includes(
                    "import-kml-button"
                  ),
              })}
            >
              Import KML
            </Button>
          </Popover>
        </div>
      </div>
    );
  };

  const renderCreateLandmark = () => {
    return (
      <div>
        <Button
          block
          className="back-button"
          onClick={() => onBackButtonClick()}
          icon={<ArrowLeftOutlined />}
        >
          BACK
        </Button>
        <div className="create-landmark">
          <div className="header">
            <label className="title">Create Landmark</label>
            <Button
              type="text"
              style={{ marginLeft: "auto" }}
              icon={<HelpIcon className="help-icon" />}
            />
          </div>
          <div className="create-landmark-body">
            <Popover
              title={PopoverTitle}
              content={contentOfPaddock}
              placement="leftTop"
              trigger="hover"
              visible={landmarkStep4Popover}
              // onVisibleChange={(visible) => console.log(visible)}
            >
              <Button
                block
                icon={<Paddock className="landmark-icon" />}
                onClick={() => {
                  onCreateLandmark("Fence", 3);
                  setCurrentStep((step) => step + 2);
                }}
                id="create-landmark-fence"
                className={clsx("sub-item full d-flex", {
                  "overlay-element-active":
                    currentStepDetails.overlayElementsId?.includes(
                      "create-landmark-fence"
                    ),
                })}
              >
                Paddock/Hard Fence
              </Button>
            </Popover>
            <Button
              block
              className="sub-item left d-flex"
              icon={<Structure className="landmark-icon" />}
              onClick={() => onCreateLandmark("Structure", 1)}
            >
              Structure
            </Button>
            <Button
              block
              className="sub-item right d-flex"
              icon={<Gate className="landmark-icon" />}
              onClick={() => onCreateLandmark("Gate", 1)}
            >
              Gate
            </Button>
            <Button
              block
              className="sub-item left d-flex"
              icon={<Corral className="landmark-icon" />}
              onClick={() => onCreateLandmark("Corral", 1)}
            >
              Corral
            </Button>
            <Popover
              title={PopoverTitle}
              content={contentOfGateway}
              placement="leftTop"
              trigger="hover"
              visible={landmarkStep10Popover}
              // onVisibleChange={(visible) => console.log(visible)}
            >
              <Button
                block
                icon={<Gateway className="landmark-icon" />}
                onClick={() => {
                  onCreateLandmark("Gateway", 1);
                  setCurrentStep((step) => step + 1);
                }}
                id="create-landmark-gateway"
                className={clsx("sub-item right d-flex", {
                  "overlay-element-active":
                    currentStepDetails.overlayElementsId?.includes(
                      "create-landmark-gateway"
                    ),
                })}
              >
                Gateway
              </Button>
            </Popover>
            <Button
              block
              className="sub-item left d-flex"
              icon={<Trail className="landmark-icon" />}
              onClick={() => onCreateLandmark("Trail", 2)}
            >
              Trail
            </Button>
            <Button
              block
              className="sub-item right d-flex"
              icon={<Road className="landmark-icon" />}
              onClick={() => onCreateLandmark("Road", 2)}
            >
              Road
            </Button>
            <Button
              block
              className="sub-item left d-flex"
              icon={<Pump className="landmark-icon" />}
              onClick={() => onCreateLandmark("Pump", 1)}
            >
              Pump
            </Button>
            <Button
              block
              className="sub-item right d-flex"
              icon={<Water className="landmark-icon" />}
              onClick={() => onCreateLandmark("Water", 3)}
            >
              Water
            </Button>
            <Button
              block
              className="sub-item left d-flex"
              icon={<River className="landmark-icon" />}
              onClick={() => onCreateLandmark("River", 2)}
            >
              River
            </Button>
            <Button
              block
              className="sub-item right d-flex"
              icon={<Burn className="landmark-icon" />}
              onClick={() => onCreateLandmark("Burn", 3)}
            >
              Burn
            </Button>
            <Button
              block
              className="sub-item left d-flex"
              icon={<Feed className="landmark-icon" />}
              onClick={() => onCreateLandmark("Feed", 1)}
            >
              Feed
            </Button>
            <Button
              block
              className="sub-item right d-flex"
              icon={<Area className="landmark-icon" />}
              onClick={() => onCreateLandmark("Area", 3)}
            >
              Area
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateLandmarkConfirm = () => {
    return (
      <div className="create-landmark-confirm">
        <div className="header">
          <label className="title">Save Landmark</label>
          <Button
            type="text"
            style={{ marginLeft: "auto" }}
            icon={<HelpIcon className="help-icon" />}
          />
        </div>
        <div className="create-landmark-confirm-body">
          <Button
            block
            className="confirm-button"
            onClick={() => {
              onCreateLandmarkConfirm();
              setCurrentStep((step) => step + 1);
            }}
          >
            Save...
          </Button>
          <Button
            block
            className="sub-item"
            // onClick={() => onBackButtonClick()}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderMenu = () => {
    switch (currentMenu) {
      case "Main":
        return renderMainMenu();
      case "CreateLandmark":
        return renderCreateLandmark();
      case "CreateLandmarkConfirm":
        return renderCreateLandmarkConfirm();
      case "CreateHerd":
        return (
          <CreateHerd
            createHerdModal={createHerdModal}
            onBackButtonClick={onBackButtonClick}
            setCurrentStep={setCurrentStep}
            currentStepDetails={currentStepDetails}
            herdStep5Popover={herdStep5Popover}
            setHerdMarkerDetails={setHerdMarkerDetails}
          />
        );
      case "HerdsToDisplay":
        return (
          <HerdsToDisplay
            onBackButtonClick={onBackButtonClick}
            herdStep9Popover={herdStep9Popover}
            setCurrentStep={setCurrentStep}
            currentStepDetails={currentStepDetails}
            herdStep10Popover={herdStep10Popover}
            herdMarkerDetails={herdMarkerDetails}
          />
        );
      case "CreateVence":
        return (
          <CreateVence
            onBackButtonClick={onBackButtonClick}
            setCurrentStep={setCurrentStep}
            currentStepDetails={currentStepDetails}
            venceStep3Popover={venceStep3Popover}
            venceStep11Popover={venceStep11Popover}
          />
        );
      case "CreateVenceConfirm":
        return (
          <CreateVenceConfirm
            onBackButtonClick={onBackButtonClick}
            setCurrentStep={setCurrentStep}
            currentStepDetails={currentStepDetails}
            venceStep6Popover={venceStep6Popover}
            createVenceModal={createVenceModal}
            venceStep7Popover={venceStep7Popover}
            venceStep8Popover={venceStep8Popover}
            venceInclusionDetails={venceInclusionDetails}
            venceExclusionDetails={venceExclusionDetails}
          />
        );
      default:
        return renderMainMenu();
    }
  };

  const newLandmarkModalTitle = (
    <div className="header">
      <div className="title">New Landmark</div>
      <Button
        className="canel-button"
        style={{ marginLeft: "auto" }}
        // onClick={() => setModalNewLandmarkVisible(false)}
        type="link"
      >
        Cancel
      </Button>
      <Button
        className="save-button"
        onClick={() => {
          onBackButtonClick();
          setCurrentStep((step) => step + 1);
        }}
      >
        Save
      </Button>
      <Button type="text" icon={<HelpIcon className="help-icon" />} />
    </div>
  );

  return (
    <div className="sidebar" ref={sidebarRef}>
      <div className="sidebar-menu">{renderMenu()}</div>
      <Modal
        visible={modalNewLandmarkVisible}
        title={newLandmarkModalTitle}
        footer={null}
        className="new-landmark-modal"
        closable={false}
        width={600}
        centered
        destroyOnClose={true} // Destroy Modal child component when closed, So that popover will be removed.
      >
        <div className="body">
          <Tabs centered={true} tabPosition="top" activeKey="1">
            <TabPane tab="INFO" key="1">
              <div className="modal-content">
                <Form layout="vertical" className="new-landmark-form">
                  <Popover
                    title={PopoverTitle}
                    content={
                      currentStepDetails.landmark === "Fence"
                        ? contentOfNewLandmarkModalOfFence
                        : contentOfNewLandmarkModalOfGateway
                    }
                    placement="left"
                    trigger="hover"
                    visible={landmarkStep7Popover}
                    // onVisibleChange={(visible) => console.log(visible)}
                  >
                    <Form.Item label="Name">
                      <Input />
                    </Form.Item>
                  </Popover>
                  <Form.Item label="Type">
                    <Select defaultValue={currentStepDetails.landmark} disabled>
                      <Select.Option value="Area">Area</Select.Option>
                      <Select.Option value="Burn">Burn</Select.Option>
                      <Select.Option value="Corral">Corral</Select.Option>
                      <Select.Option value="Feed">Feed</Select.Option>
                      <Select.Option value="Fence">Fence</Select.Option>
                      <Select.Option value="Gate">Gate</Select.Option>
                      <Select.Option value="Gateway">Gateway</Select.Option>
                      <Select.Option value="Pump">Pump</Select.Option>
                    </Select>
                  </Form.Item>
                  {currentStepDetails.landmark === "Fence" && (
                    <>
                      <Form.Item label="Color">
                        <Select>
                          <Select.Option value="Black">Black</Select.Option>
                          <Select.Option value="Blue">Blue</Select.Option>
                          <Select.Option value="Green">Green</Select.Option>
                          <Select.Option value="Aqua">Aqua</Select.Option>
                          <Select.Option value="Red">Red</Select.Option>
                          <Select.Option value="Purple">Purple</Select.Option>
                          <Select.Option value="Yellow">Yellow</Select.Option>
                        </Select>
                      </Form.Item>
                      <Form.Item>
                        <Checkbox>Show icon when small</Checkbox>
                      </Form.Item>
                      <Form.Item>
                        <Checkbox>Diagnostic report</Checkbox>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </div>
            </TabPane>
            <TabPane tab="DETAIL" key="2">
              <div className="modal-content">Detail</div>
            </TabPane>
          </Tabs>
        </div>
      </Modal>
    </div>
  );
}

export default SideBar;
